/**
 * FB onboarding funnel - step configuration.
 *
 * Facebook-ads acquisition funnel at /fb/onboarding (standalone, NOT launched
 * from Landing... it IS now the Landing CTA target, but has no auth gate). UX
 * copies the pattern the big competitors use (Keiki + Speech Blubs), which both
 * open with the lowest-friction tap (two big buttons) rather than a text field,
 * then alternate quick yes/no questions, single-selects and multi-selects, with
 * "did you know" interstitials breaking up the flow, a milestone score reveal
 * mid-funnel, and an email capture at the end. Reskinned to Timo + cream/orange.
 *
 * QUESTION CONTENT is Speech Blubs' speech/communication assessment adapted to
 * Time To Talk's autism communication-support product. Every question after the
 * name step is personalized with the child's name.
 *
 * TWO-PHASE SPLIT (2026-07-05): 270 sessions/period were converting at ~2%
 * with 56% mid-quiz abandonment across ~23 answerable questions before the
 * paywall. FUNNEL_STEPS is the short pre-paywall phase - just enough to earn
 * the "personalized plan" reveal and the emotional voice-pick payoff - ending
 * in email capture -> /fb/pricing. PROFILE_STEPS is the exact same deep
 * personalization content as before (nothing deleted), run AFTER payment via
 * FbOnboardingContinue so it never blocks a sale; skippable, and only ever
 * enriches an already-paying account.
 *
 * Screen types (renderers in ./components/screens.jsx):
 *   text | binary | single | multi | grid | interstitial | loading | score | email
 *
 * `{name}` in any string is replaced with the child's name (warm fallback when
 * skipped). Keep this file copy-only (no JSX) so the funnel is easy to review.
 * Add `image: '/fb/xyz.webp'` to any step to drop a generated illustration into
 * that screen's ImageSlot later.
 */

import timoEarlyPractice from '@/assets/timo-early-practice.webp';

/* ==================== PHASE 1: pre-paywall (short) ======================= */
export const FUNNEL_STEPS = [
  /* ============================ profile ================================== */
  // The ad click lands straight on a question. There used to be a "Start now"
  // interstitial here, added on the theory that a dry age form was causing the
  // ~95% drop on screen one. The later analysis put that drop on the continue
  // button falling below the fold on short iOS viewports, which is fixed, so the
  // interstitial was answering a problem it did not cause and is now one tap
  // between a paid click and any signal about the child. Age leads because it is
  // the cheapest question to answer and the one a parent never has to think about.
  {
    key: 'age',
    type: 'cardgrid',
    question: 'How old is your child?',
    sub: 'So Timo asks the right questions for their stage.',
    options: [
      { value: 'under3', label: 'Under 3' },
      { value: '3-4', label: '3-4' },
      { value: '5-6', label: '5-6' },
      { value: '7+', label: '7+' },
    ],
  },
  {
    key: 'gender',
    type: 'binary',
    question: 'Is your child a boy or a girl?',
    options: [
      { value: 'boy', label: 'Boy', emoji: '👦' },
      { value: 'girl', label: 'Girl', emoji: '👧' },
    ],
  },
  {
    key: 'name',
    type: 'text',
    question: 'What’s your child’s name?',
    sub: 'Or a nickname, so Timo can make it personal.',
    placeholder: 'Your child’s name',
    providesName: true,
    optional: true,
  },

  /* ======================= communication check ========================== */
  {
    key: 'words',
    type: 'single',
    question: 'About how many words can {name} say?',
    sub: 'A rough guess is perfectly fine.',
    options: [
      { value: '0', label: '0 words' },
      { value: '1-5', label: '1-5 words' },
      { value: '6-10', label: '6-10 words' },
      { value: '11-50', label: '11-50 words' },
      { value: '50+', label: '50+ words' },
      { value: 'unsure', label: 'Not sure' },
    ],
  },
  {
    key: 'sentences',
    type: 'binary',
    question: 'Does {name} use longer sentences (4-6 words) with mostly correct grammar?',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },

  {
    key: 'delay_fact',
    type: 'interstitial',
    photo: timoEarlyPractice,
    accent: '#5FBF9B',
    title: 'Starting early makes the biggest difference',
    body: 'A few minutes a day with Timo, and {name} looks forward to it.',
    cta: 'Continue',
  },

  /* ========================= score reveal =============================== */
  {
    key: 'calc',
    type: 'loading',
    title: 'Building {name}’s communication snapshot',
    labels: [
      'Understanding {name}’s needs...',
      'Comparing with milestones...',
      'Choosing the right activities...',
      'Almost ready...',
    ],
    // A five-star quote attributed to a named parent used to sit here. It is
    // pulled until there is a real customer behind it: an invented review is a
    // fine under the Omnibus directive, not just bad taste. To bring it back,
    // restore this key with the real quote; every locale file still carries the
    // `calc.review.*` translations, so it will localize the moment it returns.
  },
  {
    key: 'score',
    type: 'score',
    title: '{name}’s communication snapshot',
    areas: ['Talking', 'Understanding', 'Conversation', 'Confidence', 'Social'],
    cta: 'Build {name}’s plan',
  },

  /* ============================ voice =================================== */
  {
    key: 'voice',
    type: 'voice',
    question: 'Choose {name}’s Timo voice',
    sub: 'Tap a card to hear a preview.',
  },

  /* ============================ email =================================== */
  {
    key: 'email',
    type: 'email',
    title: 'Create your free account',
    sub: 'It saves {name}’s plan and lets you log back in anytime.',
  },
];

/* ================ PHASE 2: post-payment (deferred, skippable) ============ */
// Reached from /fb/success via FbOnboardingContinue. Same questions that used
// to sit between "sentences" and the score reveal, plus the full personalization
// block - unchanged content, just moved so it enriches a paying account instead
// of standing between an ad click and a sale.
export const PROFILE_STEPS = [
  {
    key: 'conversation',
    type: 'binary',
    question: 'Can {name} keep a conversation going without much adult help?',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
  {
    key: 'questions',
    type: 'binary',
    question: 'Does {name} enjoy asking lots of questions, like “Why is it raining?”',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
  {
    key: 'find_words',
    type: 'binary',
    question: 'Does {name} sometimes struggle to find the words in the moment?',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
  {
    key: 'sounds',
    type: 'multi',
    question: 'Which sounds can {name} say clearly?',
    sub: 'Tap all that apply - it helps Timo pick the right practice.',
    options: [
      { value: 'p_b', label: 'P, B  (pot, book)' },
      { value: 'm_n', label: 'M, N  (mat, nose)' },
      { value: 't_d', label: 'T, D  (top, dishes)' },
      { value: 'k_g', label: 'K, G  (car, go)' },
      { value: 'f_v', label: 'F, V  (fish, van)' },
      { value: 's_z', label: 'S, Z  (sun, zoo)' },
      { value: 'l', label: 'L  (lion)' },
      { value: 'r', label: 'R  (rabbit)' },
      { value: 'sh_ch', label: 'SH, CH  (shoe, chair)' },
    ],
  },
  {
    key: 'therapist',
    type: 'binary',
    question: 'Has {name} ever been seen by a speech therapist?',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
  {
    key: 'diagnosis',
    type: 'single',
    question: 'Has {name} been given a diagnosis?',
    sub: 'This stays private and only helps Timo personalize.',
    options: [
      { value: 'autism', label: 'Autism / ASD' },
      { value: 'speech_delay', label: 'Speech or language delay' },
      { value: 'adhd', label: 'ADHD' },
      { value: 'none', label: 'No diagnosis' },
      { value: 'prefer_not', label: 'Prefer not to say' },
    ],
  },
  {
    key: 'social',
    type: 'binary',
    question: 'Does {name} find group or social situations hard?',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
  {
    key: 'goals',
    type: 'multi',
    question: 'How can Timo help {name} most?',
    sub: 'Pick up to 3 - it shapes the whole plan.',
    max: 3,
    options: [
      { value: 'confidence', label: 'Build confidence when speaking', emoji: '🎙️' },
      { value: 'conversations', label: 'Practice everyday conversations', emoji: '💬' },
      { value: 'social', label: 'Handle tricky social moments', emoji: '🤝' },
      { value: 'feelings', label: 'Express feelings in a healthy way', emoji: '💖' },
      { value: 'calm', label: 'Stay calm when overwhelmed', emoji: '🌈' },
      { value: 'school', label: 'Get ready for school', emoji: '🎒' },
    ],
  },
  {
    key: 'topics',
    type: 'grid',
    question: 'Pick {name}’s favorite topics',
    sub: 'Timo weaves these into the practice.',
    options: [
      { value: 'animals', label: 'Animals', emoji: '🐶' },
      { value: 'space', label: 'Space', emoji: '🚀' },
      { value: 'vehicles', label: 'Vehicles', emoji: '🚗' },
      { value: 'music', label: 'Music', emoji: '🎵' },
      { value: 'nature', label: 'Nature', emoji: '🌳' },
      { value: 'food', label: 'Food', emoji: '🍕' },
    ],
  },
  {
    key: 'experts',
    type: 'interstitial',
    emoji: '⭐',
    accent: '#FFB020',
    title: 'Built with experts, tailored to {name}',
    body: 'Timo Helps is guided by a speech and autism-communication framework, then shaped around {name}’s answers - never one-size-fits-all.',
    quote: {
      text: 'Over 10,000 families use Timo Helps to help their child find their voice.',
      author: '4.8 average rating',
    },
    cta: 'See {name}’s plan',
  },
  {
    key: 'learn_style',
    type: 'single',
    question: 'What’s {name}’s favorite way to learn?',
    options: [
      { value: 'hands_on', label: 'Doing hands-on activities' },
      { value: 'stories', label: 'Listening to stories' },
      { value: 'video', label: 'Watching videos' },
      { value: 'games', label: 'Playing games' },
    ],
  },
  {
    key: 'strengths',
    type: 'multi',
    question: 'What are {name}’s strengths?',
    sub: 'Pick any that sound like them.',
    options: [
      { value: 'creative', label: 'Creative' },
      { value: 'curious', label: 'Curious' },
      { value: 'caring', label: 'Caring' },
      { value: 'determined', label: 'Determined' },
      { value: 'funny', label: 'Funny' },
    ],
  },
  {
    key: 'challenge',
    type: 'single',
    question: 'What’s {name}’s biggest challenge right now?',
    options: [
      { value: 'frustrated', label: 'Gets frustrated easily' },
      { value: 'distracted', label: 'Easily distracted' },
      { value: 'shy', label: 'Shy or anxious with people' },
      { value: 'overwhelmed', label: 'Gets overwhelmed' },
    ],
  },
  {
    key: 'daily_time',
    type: 'single',
    question: 'How much practice feels realistic each day?',
    sub: 'Even a few minutes adds up fast.',
    options: [
      { value: '5', label: 'Little Boost', hint: '5 min/day' },
      { value: '10', label: 'Just Right', hint: '10 min/day' },
      { value: '20', label: 'Supercharged', hint: '20 min/day' },
    ],
  },
  {
    key: 'when',
    type: 'single',
    question: 'When will {name} practice?',
    options: [
      { value: 'morning', label: 'Morning', hint: 'A calm start' },
      { value: 'afternoon', label: 'Afternoon', hint: 'A midday boost' },
      { value: 'evening', label: 'Evening', hint: 'Wind-down before bed' },
    ],
  },
  {
    key: 'who_uses',
    type: 'single',
    question: 'Who will use Timo Helps with {name}?',
    options: [
      { value: 'mom', label: 'Mom' },
      { value: 'dad', label: 'Dad' },
      { value: 'grandparent', label: 'Grandparent' },
      { value: 'specialist', label: 'A therapist or teacher' },
      { value: 'themselves', label: 'On their own' },
    ],
  },
  {
    key: 'timeline',
    type: 'single',
    question: 'How soon do you want to see progress?',
    options: [
      { value: 'weeks', label: 'In a few weeks' },
      { value: '1-2m', label: 'In 1-2 months' },
      { value: 'no_rush', label: 'No strict timeline' },
    ],
  },
  {
    key: 'main_goal',
    type: 'single',
    question: 'What’s your main goal right now?',
    options: [
      { value: 'catch_up', label: 'Help {name} catch up' },
      { value: 'on_track', label: 'Keep progress on track' },
      { value: 'confidence', label: 'Build {name}’s confidence' },
      { value: 'other', label: 'Something else' },
    ],
  },

  /* ============================ done ==================================== */
  {
    key: 'profile_done',
    type: 'interstitial',
    emoji: '🎉',
    accent: '#5FBF9B',
    title: '{name}’s full plan is ready',
    body: 'Timo has everything it needs. Let’s start practicing together.',
    cta: 'Start practicing',
  },
];

/** Answer screens that advance the numbered progress bar. */
const PROGRESS_TYPES = new Set(['text', 'binary', 'single', 'multi', 'grid', 'cardgrid', 'voice']);
export const isProgressStep = (step) => PROGRESS_TYPES.has(step.type);

/**
 * Warm generic used when the parent skips the (optional) name step. Localized,
 * otherwise a Lithuanian or Russian sentence ends with the English words "your
 * child" in the middle of it. Each form is the case the locale files actually
 * put {name} in (subject / after a preposition that takes it), so the fallback
 * reads correctly in every sentence that still carries the token.
 */
const NAME_FALLBACK = {
  en: 'your child',
  lt: 'jūsų vaikas',
  ru: 'ваш ребёнок',
  de: 'Ihr Kind',
  no: 'barnet ditt',
  es: 'tu hijo',
  fr: 'votre enfant',
};

// Read straight from storage (same key LanguageContext writes) so withName's
// signature stays untouched for its callers.
function activeLang() {
  try {
    return localStorage.getItem('ttt-language') || 'en';
  } catch {
    return 'en';
  }
}

/** Replace {name} tokens. Falls back to a warm generic when name is skipped. */
export function withName(text, name) {
  if (!text) return text;
  const safe = (name && name.trim())
    ? name.trim()
    : (NAME_FALLBACK[activeLang()] || NAME_FALLBACK.en);
  return text.replace(/\{name\}/g, safe);
}
